import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Linking,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useNavigation } from "@react-navigation/native";
import { useAuth } from "./AuthContext";
import { useTranslation } from "./i18n/useTranslation";
import { FASTAPI_BASE_URL } from "./config";

const createEmptyChild = () => ({
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  grade: "",
  age: "",
});

export default function UpgradePlanScreen() {
  const navigation = useNavigation();
  const { user, fetchWithAuth, logout } = useAuth();
  const { t } = useTranslation();

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planCode, setPlanCode] = useState("");
  const [isPurchaseFlow, setIsPurchaseFlow] = useState(false);

  const [hasDiscount, setHasDiscount] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [error, setError] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [children, setChildren] = useState([]);

  useEffect(() => {
    const loadPlans = async () => {
      setLoadingPlans(true);
      try {
        const params = new URLSearchParams();
        if (user?.role) {
          params.set("role", user.role);
        }
        const suffix = params.toString() ? `?${params.toString()}` : "";
        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/payments/plans${suffix}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(json?.detail || "Failed to load plans.");
          return;
        }
        const list = Array.isArray(json?.plans) ? json.plans : [];
        setPlans(list);
        let preselect = "";
        try {
          preselect = (await AsyncStorage.getItem("pending_plan_code")) || "";
        } catch {}
        if (preselect) {
          const found = list.find((p) => p.code === preselect);
          if (found) {
            setPlanCode(found.code);
            setIsPurchaseFlow(true);
            try {
              await AsyncStorage.removeItem("pending_plan_code");
            } catch {}
            return;
          }
        }
        if (list.length > 0 && !planCode) {
          setPlanCode(list[0].code);
        }
      } catch (e) {
        setError(String(e?.message || e));
      } finally {
        setLoadingPlans(false);
      }
    };
    loadPlans();
  }, [user?.role]);

  useEffect(() => {
    const loadSubStatus = async () => {
      try {
        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/payments/subscription-status`);
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
          setSubStatus(json);
          if (json?.exists) {
            setAutoRenew(!!json.auto_renew);
          }
        }
      } catch {}
    };
    loadSubStatus();
  }, []);

  useEffect(() => {
    const fetchEstimate = async () => {
      if (!planCode) return;
      setEstimateLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          plan_code: planCode,
        });
        if (user?.role) {
          params.set("role", user.role);
        }
        if (hasDiscount && discountInput.trim()) {
          params.set("discount_code", discountInput.trim().toUpperCase());
        }
        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/payments/estimate?${params.toString()}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setEstimate(null);
          setError(json?.detail || "Could not estimate total.");
          return;
        }
        setEstimate(json);
        setError("");
      } catch (e) {
        setEstimate(null);
        setError(String(e?.message || e));
      } finally {
        setEstimateLoading(false);
      }
    };
    fetchEstimate();
  }, [planCode, hasDiscount, discountInput]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.code === planCode) || null,
    [plans, planCode]
  );
  const isSubscriptionRestricted = !!user?.subscription_restricted;
  // WP7: a restricted user has no AITutor route to fall back to, so Back must
  // never strand them - go back if there is history, else log out.
  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else if (isSubscriptionRestricted) {
      logout();
    } else {
      navigation.replace("AITutor");
    }
  };
  const isInternalAdmin = !!user?.is_internal_admin;
  const isParent = (user?.role || "").toLowerCase() === "parent";
  const existingChildrenCount = Number(user?.parent_children_count || 0);
  const remainingChildrenSlots = Math.max(0, 2 - existingChildrenCount);
  const hasExistingSubscription = !!subStatus?.exists;
  const hasExistingAutoRenew = hasExistingSubscription && !!subStatus?.auto_renew;
  const showCancelAutoRenew = hasExistingAutoRenew;

  const formatMoney = (cents, currency = "USD") =>
    `$${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;

  const updateChild = (idx, field, value) => {
    setChildren((prev) => prev.map((child, i) => (i === idx ? { ...child, [field]: value } : child)));
  };

  const addChild = () => {
    setChildren((prev) => {
      if (prev.length >= remainingChildrenSlots) return prev;
      return [...prev, createEmptyChild()];
    });
  };

  const removeChild = (idx) => {
    setChildren((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCheckout = async () => {
    if (!planCode) {
      setError("Please choose a plan.");
      return;
    }
    const cleanedChildren =
      isParent && remainingChildrenSlots > 0
        ? children
            .map((child) => ({
              first_name: (child.first_name || "").trim(),
              last_name: (child.last_name || "").trim(),
              email: (child.email || "").trim().toLowerCase(),
              password: child.password || "",
              grade: child.grade ? Number.parseInt(child.grade, 10) : 9,
              age: child.age ? Number.parseInt(child.age, 10) : null,
            }))
            .filter((child) => child.first_name || child.last_name || child.email || child.password)
        : [];
    for (const child of cleanedChildren) {
      if (!child.first_name || !child.last_name || !child.email || !child.password) {
        setError("Each child needs first name, last name, email, and password.");
        return;
      }
    }
    setCheckoutLoading(true);
    setError("");
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const successUrl = origin ? `${origin}/?payment_success=1` : "";
      const cancelUrl = origin ? `${origin}/?payment_cancel=1` : "";

      const payload = {
        plan_code: planCode,
        discount_code: hasDiscount ? discountInput.trim().toUpperCase() : null,
        success_url: successUrl,
        cancel_url: cancelUrl,
        auto_renew: !!autoRenew,
        children: cleanedChildren,
      };
      const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/payments/checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.detail || "Failed to start checkout.");
        return;
      }
      const url = json?.url;
      if (!url) {
        setError("Checkout URL missing.");
        return;
      }
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.location.assign(url);
      } else {
        Linking.openURL(url);
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleToggleAutoRenew = () => {
    setError("");
    setAutoRenew((prev) => !prev);
  };

  const handleCancelAutoRenew = async () => {
    try {
      const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/payments/subscription/cancel`, {
        method: "POST",
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.detail || "Failed to cancel auto-renew.");
        return;
      }
      setAutoRenew(false);
      setSubStatus((s) => (s ? { ...s, auto_renew: false, status: "canceled" } : s));
      setError("");
    } catch (e) {
      setError(String(e?.message || e));
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.card}>
        <Text style={styles.title}>{isPurchaseFlow ? "Proceed With Purchase" : "Upgrade Your Plan"}</Text>
        <Text style={styles.subtitle}>
          {isPurchaseFlow
            ? "Confirm your plan selection and complete checkout. You can add a discount code if you have one."
            : "Choose a paid plan and complete checkout. Your card will be saved for future renewals."}
        </Text>

        {isSubscriptionRestricted ? (
          <View style={styles.restrictedNotice}>
            <Text style={styles.restrictedTitle}>Subscription access is paused</Text>
            <Text style={styles.restrictedText}>
              {user?.subscription_end_status_text || "Your subscription has expired."}
            </Text>
            <Text style={styles.restrictedText}>
              {user?.subscription_cutoff_status_text || "Upgrade to restore full access."}
            </Text>
            <Text style={styles.restrictedText}>
              {t("Your subscription has ended. Choose a plan to continue.")}
            </Text>
            {isInternalAdmin ? (
              <TouchableOpacity
                onPress={() => navigation.navigate("InternalAdmin")}
                style={styles.adminShortcutBtn}
              >
                <Text style={styles.adminShortcutText}>Open Internal Admin Panel</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your Details</Text>
          <View style={styles.row}>
            <View style={styles.half}>
              <Text style={styles.label}>Name</Text>
              <TextInput style={styles.input} value={user?.name || ""} editable={false} />
            </View>
            <View style={styles.half}>
              <Text style={styles.label}>Email</Text>
              <TextInput style={styles.input} value={user?.email || ""} editable={false} />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Plan</Text>
          {loadingPlans ? (
            <ActivityIndicator />
          ) : (
            <>
              {Platform.OS === "web" ? (
                <select
                  value={planCode}
                  onChange={(e) => setPlanCode(e.target.value)}
                  style={styles.select}
                >
                  {plans.map((p) => (
                    <option key={p.code} value={p.code}>
                      {`${p.name} - ${formatMoney(p.price_cents, p.currency)}`}
                    </option>
                  ))}
                </select>
              ) : (
                <View style={styles.planList}>
                  {plans.map((p) => (
                    <TouchableOpacity
                      key={p.code}
                      onPress={() => setPlanCode(p.code)}
                      style={[
                        styles.planPill,
                        p.code === planCode && styles.planPillActive,
                      ]}
                    >
                      <Text style={styles.planPillText}>
                        {p.name} ({formatMoney(p.price_cents, p.currency)})
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Auto-Renew</Text>
          {showCancelAutoRenew ? (
            <>
              <TouchableOpacity onPress={handleCancelAutoRenew} style={styles.cancelAutoRenewBtn}>
                <Text style={styles.cancelAutoRenewText}>Cancel Current Auto-Renew</Text>
              </TouchableOpacity>
              <Text style={styles.cancelAutoRenewNote}>
                Auto-renew is currently enabled for your subscription. Canceling turns it off immediately for the next cycle,
                and your access stays active until the current plan ends.
              </Text>
            </>
          ) : (
            <>
              <View style={styles.discountRow}>
                <TouchableOpacity
                  onPress={handleToggleAutoRenew}
                  style={[styles.checkbox, autoRenew && styles.checkboxChecked]}
                >
                  {autoRenew ? <Text style={styles.checkboxTick}>{"\u2713"}</Text> : null}
                </TouchableOpacity>
                <Text style={styles.label}>Enable auto-renew after expiry</Text>
              </View>
              <Text style={styles.cancelAutoRenewNote}>
                {hasExistingSubscription
                  ? "This will be saved when you complete checkout for the selected plan."
                  : "This will be saved when you complete checkout."}
              </Text>
            </>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Discount</Text>
          <View style={styles.discountRow}>
            <TouchableOpacity
              onPress={() => setHasDiscount(!hasDiscount)}
              style={[styles.checkbox, hasDiscount && styles.checkboxChecked]}
            >
              {hasDiscount ? <Text style={styles.checkboxTick}>{"\u2713"}</Text> : null}
            </TouchableOpacity>
            <Text style={styles.label}>Have a discount code?</Text>
          </View>
          {hasDiscount && (
            <TextInput
              style={styles.input}
              placeholder="Enter discount code"
              value={discountInput}
              onChangeText={setDiscountInput}
              autoCapitalize="characters"
            />
          )}
        </View>

        {isParent && remainingChildrenSlots > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Children</Text>
            <Text style={styles.cancelAutoRenewNote}>
              You currently have {existingChildrenCount} linked {existingChildrenCount === 1 ? "child" : "children"}.
              You can add up to {remainingChildrenSlots} more during this upgrade.
            </Text>
            {children.map((child, idx) => (
              <View key={`upgrade-child-${idx}`} style={styles.childCard}>
                <View style={styles.childHeader}>
                  <Text style={styles.childTitle}>Child {existingChildrenCount + idx + 1}</Text>
                  <TouchableOpacity onPress={() => removeChild(idx)}>
                    <Text style={styles.childRemove}>Remove</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.row}>
                  <View style={styles.half}>
                    <TextInput
                      style={styles.input}
                      placeholder="First name"
                      value={child.first_name}
                      onChangeText={(value) => updateChild(idx, "first_name", value)}
                    />
                  </View>
                  <View style={styles.half}>
                    <TextInput
                      style={styles.input}
                      placeholder="Last name"
                      value={child.last_name}
                      onChangeText={(value) => updateChild(idx, "last_name", value)}
                    />
                  </View>
                </View>
                <TextInput
                  style={styles.input}
                  placeholder="Child email"
                  value={child.email}
                  onChangeText={(value) => updateChild(idx, "email", value)}
                  autoCapitalize="none"
                />
                <TextInput
                  style={styles.input}
                  placeholder="Child password"
                  value={child.password}
                  onChangeText={(value) => updateChild(idx, "password", value)}
                  secureTextEntry
                />
                <View style={styles.row}>
                  <View style={styles.half}>
                    <TextInput
                      style={styles.input}
                      placeholder="Grade"
                      value={child.grade}
                      onChangeText={(value) => updateChild(idx, "grade", value.replace(/[^0-9]/g, ""))}
                      keyboardType="number-pad"
                    />
                  </View>
                  <View style={styles.half}>
                    <TextInput
                      style={styles.input}
                      placeholder="Age (optional)"
                      value={child.age}
                      onChangeText={(value) => updateChild(idx, "age", value.replace(/[^0-9]/g, ""))}
                      keyboardType="number-pad"
                    />
                  </View>
                </View>
              </View>
            ))}
            <TouchableOpacity
              onPress={addChild}
              disabled={children.length >= remainingChildrenSlots}
              style={[styles.addChildBtn, children.length >= remainingChildrenSlots && styles.ctaBtnDisabled]}
            >
              <Text style={styles.addChildText}>Add child</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Summary</Text>
          {estimateLoading ? (
            <ActivityIndicator />
          ) : (
            <View style={styles.summaryBox}>
              <Text style={styles.summaryRow}>
                Subtotal: {formatMoney(estimate?.amount_subtotal_cents, estimate?.currency || "USD")}
              </Text>
              <Text style={styles.summaryRow}>
                Discount: -{formatMoney(estimate?.discount_amount_cents, estimate?.currency || "USD")}
              </Text>
              <Text style={[styles.summaryRow, styles.summaryTotal]}>
                Total: {formatMoney(estimate?.amount_total_cents, estimate?.currency || "USD")}
              </Text>
              <Text style={styles.taxNote}>
                Tax will be calculated at checkout based on billing address.
              </Text>
            </View>
          )}
        </View>

        {!!error && <Text style={styles.errorText}>{error}</Text>}

        <TouchableOpacity
          onPress={handleCheckout}
          disabled={checkoutLoading || loadingPlans || !selectedPlan}
          style={[styles.ctaBtn, (checkoutLoading || loadingPlans || !selectedPlan) && styles.ctaBtnDisabled]}
        >
          <Text style={styles.ctaText}>
            {checkoutLoading ? "Redirecting..." : (isPurchaseFlow ? "Proceed to Purchase" : "Proceed to Pay")}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity onPress={handleBack} style={styles.backLink}>
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: "#f3f6fb",
    alignItems: "center",
    paddingVertical: 20,
  },
  card: {
    width: "70%",
    maxWidth: 920,
    minWidth: 320,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 6,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    color: "#475569",
    textAlign: "center",
    marginBottom: 16,
  },
  restrictedNotice: {
    marginBottom: 6,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#fdba74",
    backgroundColor: "#fff7ed",
  },
  restrictedTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#9a3412",
    marginBottom: 6,
  },
  restrictedText: {
    fontSize: 13,
    color: "#7c2d12",
    lineHeight: 18,
  },
  adminShortcutBtn: {
    alignSelf: "flex-start",
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: "#1d4ed8",
  },
  adminShortcutText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 12,
  },
  section: {
    marginTop: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "700",
    marginBottom: 8,
    color: "#0f172a",
  },
  row: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  half: {
    flex: 1,
    minWidth: 240,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    color: "#334155",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: 12,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  select: {
    width: "100%",
    height: 44,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#cbd5e1",
    borderRadius: 8,
    backgroundColor: "#fff",
    paddingLeft: 10,
    fontSize: 14,
  },
  planList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  planPill: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
  },
  planPillActive: {
    borderColor: "#2563eb",
    backgroundColor: "#e0ecff",
  },
  planPillText: {
    fontSize: 12,
    color: "#0f172a",
  },
  childCard: {
    marginTop: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 10,
    backgroundColor: "#f8fafc",
  },
  childHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  childTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#0f172a",
  },
  childRemove: {
    color: "#b91c1c",
    fontWeight: "600",
    fontSize: 12,
  },
  addChildBtn: {
    alignSelf: "flex-start",
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
  },
  addChildText: {
    color: "#1d4ed8",
    fontWeight: "700",
    fontSize: 12,
  },
  discountRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#2563eb",
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: {
    backgroundColor: "#2563eb",
  },
  checkboxTick: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "700",
  },
  cancelAutoRenewBtn: {
    alignSelf: "flex-start",
    marginTop: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#f59e0b",
    backgroundColor: "#fff7ed",
  },
  cancelAutoRenewNote: {
    marginTop: 6,
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 16,
  },
  cancelAutoRenewText: {
    color: "#9a3412",
    fontWeight: "700",
    fontSize: 12,
  },
  summaryBox: {
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  summaryRow: {
    fontSize: 13,
    color: "#1e293b",
    marginBottom: 4,
  },
  summaryTotal: {
    fontWeight: "700",
  },
  taxNote: {
    marginTop: 6,
    fontSize: 12,
    color: "#64748b",
  },
  errorText: {
    color: "#dc2626",
    marginTop: 10,
    textAlign: "center",
  },
  ctaBtn: {
    marginTop: 16,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: "#2563eb",
    alignItems: "center",
  },
  ctaBtnDisabled: {
    opacity: 0.6,
  },
  ctaText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 15,
  },
  backLink: {
    marginTop: 12,
    alignItems: "center",
  },
  backText: {
    color: "#2563eb",
    fontWeight: "600",
  },
});
